import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { isMarketplaceAccount, isMarketplaceTheme } from '@/lib/marketplace';
import { normalizeCurrency } from '@/lib/currency';
import { resolveStoreName } from '@/lib/display';
import AnnonceCard from '@/components/feature/marketplace/AnnonceCard';
import MarketplaceFilters from '@/components/feature/marketplace/MarketplaceFilters';
import ProgressiveFooter from '@/components/feature/marketplace/ProgressiveFooter';
import { useProgressiveList } from '@/hooks/useProgressiveList';
import {
  fetchMarketplaceSellerIds,
  fetchMarketplaceCategories,
  fetchMarketplaceAnnonces,
  distinctCities,
  type MarketplaceAnnonce,
  type MarketplaceCategory,
} from '@/lib/marketplaceCatalog';

// ─────────────────────────────────────────────────────────────
// Page publique « Annonces » (marketplace mixte).
// Liste les annonces des vendeurs (hors Produits / Services) avec
// filtre par nom, ville et catégorie.
// ─────────────────────────────────────────────────────────────

export default function AnnoncesPage() {
  const { tenant, theme, isTenant } = useTenant();
  const merchantId = isTenant && tenant ? Number(tenant.id) : null;
  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  const storeName = resolveStoreName(tenant, theme);
  const isMarketplace =
    Boolean(tenant) &&
    (isMarketplaceTheme(theme) || isMarketplaceAccount(tenant?.type, tenant?.typecompte));

  const [annonces, setAnnonces] = useState<MarketplaceAnnonce[]>([]);
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    document.title = `Annonces — ${storeName}`;
  }, [storeName]);

  useEffect(() => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sellerIds = await fetchMarketplaceSellerIds(merchantId);
        const [list, cats] = await Promise.all([
          fetchMarketplaceAnnonces(merchantId, { kind: 'autres', sellerIds }),
          fetchMarketplaceCategories(merchantId),
        ]);
        if (cancelled) return;
        setAnnonces(list);
        setCategories(cats);
      } catch {
        // silencieux
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId]);

  const cities = useMemo(() => distinctCities(annonces), [annonces]);

  // Catégories racines pour le filtre ; table complète pour les noms affichés
  // sur les cartes (chaîne Catégorie › Sous-catégorie › Sous-sous-catégorie).
  const rootCategories = useMemo(() => categories.filter((c) => !c.parent_id), [categories]);
  const categoryNameMap = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c.nom])) as Record<number, string>,
    [categories],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return annonces.filter((a) => {
      if (q && ![(a.titre || ''), (a.description || '')].join(' ').toLowerCase().includes(q)) return false;
      if (city && (a.ville || '').trim() !== city) return false;
      if (category && String(a.idcategorie ?? '') !== category) return false;
      return true;
    });
  }, [annonces, query, city, category]);

  const pager = useProgressiveList(filtered, 12);

  return (
    <div className="min-h-screen bg-background-50">
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/60 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold uppercase tracking-wider mb-3">
            <i className="ri-megaphone-line"></i>
            Marketplace
          </span>
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">Annonces</h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            Toutes les annonces publiées par les vendeurs de {storeName}.
          </p>
          {isMarketplace && (
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2">
              <Link
                to="/publier-annonce"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-add-circle-line"></i>
                Publier une annonce
              </Link>
              <span className="text-[11px] text-foreground-400">
                Sans compte · 1 photo offerte
              </span>
            </div>
          )}
        </div>
      </section>

      <section className="pb-16">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <MarketplaceFilters
              query={query}
              onQueryChange={setQuery}
              city={city}
              onCityChange={setCity}
              cities={cities}
              category={category}
              onCategoryChange={setCategory}
              categories={rootCategories}
              placeholder="Rechercher une annonce par nom…"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-megaphone-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">Aucune annonce ne correspond à votre recherche</p>
              {isMarketplace && (
                <Link
                  to="/publier-annonce"
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-add-circle-line"></i>
                  Publier la première annonce
                </Link>
              )}
            </div>
          ) : (
            <>
              <p className="text-xs text-foreground-400 mb-4">
                {filtered.length} annonce{filtered.length > 1 ? 's' : ''} trouvée{filtered.length > 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                {pager.visible.map((a) => (
                  <AnnonceCard key={a.id} annonce={a} monaie={monaie} categoryNames={categoryNameMap} />
                ))}
              </div>
              <ProgressiveFooter
                hasMore={pager.hasMore}
                shown={pager.visible.length}
                total={pager.total}
                remaining={pager.remaining}
                loadMore={pager.loadMore}
                sentinelRef={pager.sentinelRef}
                label="annonces"
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
}